import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, ChevronRight, Clock, Star } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServicesPage = () => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // SEO title
    document.title = "Nos Prestations et Forfaits | Fast&Car - Import Europe & Corée";
    
    const fetchPacks = async () => {
      try {
        const response = await axios.get(`${API}/packs`);
        setPacks(response.data);
      } catch (error) {
        console.error("Error fetching packs:", error);
        // Fallback data
        setPacks([
          {
            name: "Pack Essentiel",
            price: "990€",
            duration: null,
            features: [
              "Définition de vos critères",
              "Recherche active de véhicules",
              "Présélection d'annonces",
              "Échanges avec les vendeurs",
              "Conseils pour votre décision",
              "Paiement en une fois à la signature",
            ],
            highlight: false,
          },
          {
            name: "Pack Avancé",
            price: "1 290€",
            duration: null,
            features: [
              "Tout le Pack Essentiel",
              "Prise de contact avec les vendeurs",
              "Assistance dans les discussions",
              "Accompagnement à la négociation",
              "Vous restez décisionnaire final",
              "Paiement en une fois à la signature",
            ],
            highlight: false,
          },
          {
            name: "Pack Sécurité",
            price: "2 490€",
            duration: null,
            features: [
              "Recherche personnalisée",
              "Analyse approfondie",
              "Sécurisation administrative",
              "1 inspection indépendante incluse",
              "Analyse du rapport d'inspection",
              "30% à la signature, solde avant inspection",
            ],
            highlight: true,
          },
          {
            name: "Pack Premium",
            price: "3 990€",
            duration: null,
            features: [
              "Tout le Pack Sécurité",
              "Organisation du rapatriement",
              "Coordination logistique complète",
              "Assistance administrative import",
              "Livraison au siège Fast&Car",
              "30% à la signature, solde à la réservation",
            ],
            highlight: false,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchPacks();
  }, []);

  return (
    <div data-testid="services-page" className="pt-32 md:pt-40">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-carbon-paper border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
              Nos Offres
            </p>
            <h1
              className="font-unbounded text-4xl md:text-6xl font-black text-white leading-tight mb-6"
              data-testid="services-title"
            >
              Forfaits
              <br />
              <span className="text-neon-lime text-glow">Import Europe</span>
            </h1>
            <p className="font-manrope text-lg md:text-xl text-zinc-400 leading-relaxed">
              4 niveaux d'accompagnement adaptés à vos besoins. Du simple conseil
              à l'import clé en main, choisissez la formule qui vous convient.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="bg-zinc-900 border border-zinc-800 p-8 animate-pulse"
                >
                  <div className="h-6 bg-zinc-800 mb-4 w-3/4" />
                  <div className="h-10 bg-zinc-800 mb-6 w-1/2" />
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((j) => (
                      <div key={j} className="h-4 bg-zinc-800 w-full" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
              data-testid="pricing-grid"
            >
              {packs.map((pack, index) => (
                <div
                  key={pack.name}
                  data-testid={`pricing-card-${index}`}
                  className={`relative bg-zinc-900 border p-8 transition-all duration-300 hover:-translate-y-2 ${
                    pack.highlight
                      ? "border-neon-lime pricing-highlight"
                      : "border-zinc-800 hover:border-zinc-700"
                  }`}
                >
                  {/* Header */}
                  <div className="mb-6">
                    <h3 className="font-unbounded text-lg font-bold text-white mb-2">
                      {pack.name}
                    </h3>
                    <div className="flex items-baseline gap-2">
                      <span className="font-unbounded text-4xl font-black text-white">
                        {pack.price}
                      </span>
                    </div>
                    {pack.duration && (
                      <div className="flex items-center gap-2 mt-2 text-zinc-500 text-sm">
                        <Clock className="w-4 h-4" />
                        <span>Durée: {pack.duration}</span>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {pack.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check
                          className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            pack.highlight ? "text-neon-lime" : "text-zinc-600"
                          }`}
                        />
                        <span className="text-zinc-400 text-sm leading-relaxed">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Link to="/contact" state={{ selectedPack: pack.name }}>
                    <Button
                      data-testid={`pricing-cta-${index}`}
                      className={`w-full font-unbounded font-bold uppercase tracking-wider py-4 ${
                        pack.highlight
                          ? "bg-neon-lime text-black hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
                          : "bg-zinc-800 text-white hover:bg-zinc-700"
                      }`}
                    >
                      Choisir
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* What's included */}
      <section className="py-24 md:py-32 bg-carbon-paper border-y border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            <div>
              <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
                Ce qui est inclus
              </p>
              <h2 className="font-unbounded text-3xl md:text-4xl font-bold text-white mb-6">
                Votre accompagnement
                <br />
                <span className="metallic-text">en détail</span>
              </h2>
              <p className="font-manrope text-zinc-400 leading-relaxed mb-8">
                Quel que soit le forfait choisi, vous bénéficiez de mon expertise
                de mécanicien et de mon réseau de partenaires européens pour
                trouver le véhicule idéal.
              </p>
              <Link to="/process">
                <Button
                  data-testid="included-cta"
                  variant="outline"
                  className="border-zinc-700 text-white font-unbounded font-bold uppercase tracking-wider px-8 py-4 hover:bg-zinc-800"
                >
                  <span>Voir le process détaillé</span>
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                {
                  title: "Expertise Mécanique",
                  desc: "Vérification technique par un professionnel diplômé",
                },
                {
                  title: "Réseau Européen",
                  desc: "Accès aux meilleures annonces du marché",
                },
                {
                  title: "Transparence Totale",
                  desc: "Suivi en temps réel de votre recherche",
                },
                {
                  title: "Support Dédié",
                  desc: "Un interlocuteur unique du début à la fin",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-zinc-700 transition-colors"
                >
                  <div className="w-10 h-10 border border-neon-lime/50 flex items-center justify-center mb-4">
                    <Star className="w-5 h-5 text-neon-lime" />
                  </div>
                  <h4 className="font-unbounded text-white font-bold mb-2">
                    {item.title}
                  </h4>
                  <p className="text-zinc-500 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Korea Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-zinc-900 border border-zinc-800 p-8 md:p-12 lg:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-5xl">🇰🇷</span>
                  <h2 className="font-unbounded text-3xl md:text-4xl font-bold text-white">
                    Import Corée du Sud
                  </h2>
                </div>
                <p className="font-manrope text-zinc-400 text-lg leading-relaxed mb-6">
                  Les importations depuis la Corée du Sud sont étudiées au cas par
                  cas selon vos besoins spécifiques. Hyundai, Kia, Genesis... Je
                  m'occupe de tout, des recherches aux formalités douanières.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    "Recherche sur mesure",
                    "Gestion des formalités d'import",
                    "Accompagnement douanier",
                    "Livraison en France",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-zinc-300">
                      <Check className="w-5 h-5 text-neon-lime" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact">
                  <Button
                    data-testid="korea-section-cta"
                    className="bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider px-6 sm:px-8 py-4 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
                  >
                    <span className="hidden sm:inline">Demander un devis personnalisé</span>
                    <span className="sm:hidden flex flex-col leading-tight text-sm">
                      <span>Devis</span>
                      <span>personnalisé</span>
                    </span>
                  </Button>
                </Link>
              </div>
              <div className="relative hidden lg:block">
                <div className="aspect-square bg-zinc-800 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1623944156984-130d29e4f9ad?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Import Corée du Sud"
                    className="w-full h-full object-cover"
                    data-testid="korea-image"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-neon-lime/20 -z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-24 md:py-32 bg-carbon-paper border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-unbounded text-3xl md:text-5xl font-bold text-white mb-6">
            Des questions sur les{" "}
            <span className="text-neon-lime">forfaits</span> ?
          </h2>
          <p className="font-manrope text-zinc-400 text-lg max-w-2xl mx-auto mb-10">
            Chaque projet est unique. Contactez-moi pour discuter de vos besoins
            et trouver la formule adaptée à votre situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                data-testid="faq-cta-contact"
                className="bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider px-10 py-6 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)] text-base"
              >
                <span>Me contacter</span>
              </Button>
            </Link>
            <a href="tel:0768013271">
              <Button
                data-testid="faq-cta-phone"
                variant="outline"
                className="border-zinc-700 text-white font-unbounded font-bold uppercase tracking-wider px-10 py-6 hover:bg-zinc-800 text-base"
              >
                <span>07 68 01 32 71</span>
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
